import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { FaPlay } from "react-icons/fa";

const slides = [
  {
    title: 'Document Collection',
    slideWord: 'COLLECTION',
    description: 'Seamlessly gather documents from multiple sources',
    borderGradient: 'linear-gradient(135deg, rgb(150, 255, 240) 0%, rgb(50, 150, 140) 100%)', // Light teal to dark teal
    icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="11" width="18" height="10" rx="2" stroke="#fff" strokeWidth="2" /><path d="M7 11V7a5 5 0 0110 0v4" stroke="#fff" strokeWidth="2" /></svg>
  },
  {
    title: 'Signature Collection',
    slideWord: 'SIGNING',
    description: 'Digital signatures made simple and secure',
    borderGradient: 'linear-gradient(135deg, rgb(140, 180, 210) 0%, rgb(30, 70, 100) 100%)', // Light blue-grey to dark blue-grey
    icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 17L17 7M7 7h10v10" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
  },
  {
    title: 'Templating',
    slideWord: 'TEMPLATING',
    description: 'Create standardized document templates',
    borderGradient: 'linear-gradient(135deg, rgb(200, 150, 180) 0%, rgb(100, 50, 120) 100%)', // Light pink to dark purple
    icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="11" width="18" height="10" rx="2" stroke="#fff" strokeWidth="2" /><path d="M7 11V7a5 5 0 0110 0v4" stroke="#fff" strokeWidth="2" /></svg>
  },
  {
    title: 'Fast Search',
    slideWord: 'SEARCHING',
    description: 'Quickly search through documents',
    borderGradient: 'linear-gradient(135deg, rgb(150, 240, 200) 0%, rgb(50, 140, 100) 100%)', // Light teal to dark green
    icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="11" cy="11" r="7" stroke="#fff" strokeWidth="2" /><path d="M21 21l-4.35-4.35" stroke="#fff" strokeWidth="2" strokeLinecap="round" /></svg>
  },
  {
    title: 'Secure',
    slideWord: 'SECURITY',
    description: 'Keep your documents safe',
    borderGradient: 'linear-gradient(135deg, rgb(170, 140, 200) 0%, rgb(70, 40, 100) 100%)', // Light purple to dark purple
    icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="11" width="18" height="10" rx="2" stroke="#fff" strokeWidth="2" /><path d="M7 11V7a5 5 0 0110 0v4" stroke="#fff" strokeWidth="2" /></svg>
  },
  {
    title: 'Document Management',
    slideWord: 'MANAGEMENT',
    description: 'Organize and manage all your documents',
    borderGradient: 'linear-gradient(135deg, rgb(100, 150, 220) 0%, rgb(20, 60, 120) 100%)', // Light blue to dark blue
    icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="11" width="18" height="10" rx="2" stroke="#fff" strokeWidth="2" /><path d="M7 11V7a5 5 0 0110 0v4" stroke="#fff" strokeWidth="2" /></svg>
  },
  {
    title: 'Document Tagging',
    slideWord: 'TAGGING',
    description: 'Smart categorization for easy organization',
    borderGradient: 'linear-gradient(135deg, rgb(200, 140, 180) 0%, rgb(100, 40, 120) 100%)', // Light pink to dark purple
    icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="11" width="18" height="10" rx="2" stroke="#fff" strokeWidth="2" /><path d="M7 11V7a5 5 0 0110 0v4" stroke="#fff" strokeWidth="2" /></svg>
  },
];

const Hero = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentWord = slides[currentIndex].slideWord;
    let timeout;

    if (isTyping) {
      if (displayText.length < currentWord.length) {
        timeout = setTimeout(() => {
          setDisplayText(currentWord.substring(0, displayText.length + 1));
        }, 100);
      } else {
        timeout = setTimeout(() => {
          setIsTyping(false);
          setIsDeleting(true);
        }, 1500);
      }
    } else if (isDeleting) {
      if (displayText.length > 0) {
        timeout = setTimeout(() => {
          setDisplayText(displayText.substring(0, displayText.length - 1));
        }, 50);
      } else {
        setIsDeleting(false);
        setIsTyping(true);
        setCurrentIndex((prev) => (prev + 1) % slides.length);
      }
    }

    return () => clearTimeout(timeout);
  }, [displayText, currentIndex, isTyping, isDeleting]);

  return (
    <section className="hero-section hero-bg-animated">
      <div className="container">
        <div className="row">
          <div className="col-lg-6 hero-content">
            <h1 className="hero-title">
              DOCUMENT{" "}
              <span className="sliding-word colorful-text">{displayText}</span>
              <br />
              <span className="hero-title-span">REIMAGINED</span>
            </h1>
            <p className="hero-subtitle">
              Automate, organize, and collaborate on documents with intelligent
              AI - secure, scalable, and future-ready
            </p>
            <div className="hero-buttons d-flex gap-3">
              <NavLink
                to="https://app.docusafe.ai/sign-up"
                target="_blank"
                className="btn-gradient"
              >
                Get Started
                <span className="arrow-icon">→</span>
              </NavLink>
              <NavLink
                to="https://youtu.be/HGiBGIGzjf8?si=WH-tqyisAQpxX-VZ"
                target="_blank"
                className="btn-demo"
              >
                <FaPlay color="#393f7b" className="me-3" />
                Watch Demo
              </NavLink>
            </div>
          </div>
          <div className="col-lg-6 hero-featuregrid-col">
            <div
              className="carousel-circle"
              style={{
                background: slides[currentIndex].borderGradient,
              }}
            >
              <div className="carousel-circle-content">
                <div
                  className="carousel-content"
                >
                  <div
                    className="carousel-circle-icon"
                    style={{
                      background: slides[currentIndex].borderGradient,
                    }}
                  >
                    {slides[currentIndex].icon}
                  </div>
                  <h2 className="carousel-circle-content-title">
                    {slides[currentIndex].title}
                  </h2>
                  <p className="carousel-circle-content-description">
                    {slides[currentIndex].description}
                  </p>
                </div>
              </div>
            </div>
            <div
              className="carousel-dots"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                margin: "8px 0",
              }}
            >
              {slides.map((_, index) => (
                <span
                  key={index}
                  className={`dot ${currentIndex === index ? "active" : ""}`}
                  style={{
                    display: "inline-block",
                    width: currentIndex === index ? "24px" : "8px",
                    height: "8px",
                    borderRadius: currentIndex === index ? "4px" : "50%",
                    margin: "0 4px",
                    background:
                      currentIndex === index
                        ? slides[index].borderGradient
                        : "#d1d1d1",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                  onClick={() => setCurrentIndex(index)}
                ></span>
              ))}
            </div>
            {/* <div className='side-buttons'>
              <div className="side-button left">Smart AI</div>
              <div className="side-button right-top">Fast Search</div>
              <div className="side-button right-bottom">Secure</div>
            </div> */}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;